import React from 'react';
import { arrayOf, bool, func, object, shape, string } from 'prop-types';
import { I18n as I } from 'react-redux-i18n';
import Reorder from 'react-reorder';
import { Modal, Radio } from 'antd';
import { isFunc, LoaderOverlay } from 'vdlib';
import ImageRectangleDrawer from './ImageRectangleDrawer';
import { defaultMode, initialEditState, modes } from './constants';
import { useReorder } from './hooks';

const redColor = '#d62828';
const styles = {
  modal: { height: '85%' },
  modalBody: { height: 'calc(100% - 110px)' },
  trashButton: {
    borderColor: redColor,
    backgroundColor: '#FFF',
    cursor: 'not-allowed',
    float: 'right',
    marginRight: 30,
    opacity: 0.5,
    marginTop: -1,
  },
  trashButtonIcon: {
    color: redColor,
    fontSize: '20px',
  },
};
styles.activeTrashButton = {
  ...styles.trashButton,
  cursor: 'default',
  opacity: 1,
};

const ImageBlock = ({ checked, onCheck, onClick, thumbnail }) => (
  <div className="auction-reorder-image" onClick={onClick}>
    {isFunc(onCheck) && (
      <div className="auction-image-checkbox-wrap bg-red" onClick={onCheck}>
        <input checked={checked} type="checkbox" />
      </div>
    )}
    <img src={thumbnail} alt="" />
  </div>
);

const ManageImagesModal = ({
  deleteImagesPending,
  imageUrls,
  onClearRemoveImageLicensePlate,
  onDelete,
  onHide,
  onRemoveLicensePlate,
  onReorderImages,
  removeImageLicensePlate,
  removeImageLicensePlatePending,
  reorderImages,
  reorderImagesError,
  reorderImagesPending,
  show,
  folderId,
}) => {
  const [mode, setMode] = React.useState(modes.Blur);
  const [selectedImages, setSelectedImages] = React.useState([]);
  const [fullModalEntered, setFullModalEntered] = React.useState(false);
  const [edit, setEdit] = React.useState(initialEditState);
  const [drawArea, setDrawArea] = React.useState(null);

  const editImageUrl = React.useMemo(
    () =>
      imageUrls && edit.imageId && imageUrls.find(iu => iu.id === edit.imageId),
    [edit, imageUrls]
  );

  const handleDelete = React.useCallback(() => {
    if (selectedImages && selectedImages.length > 0 && folderId)
      onDelete(folderId, selectedImages);
  }, [selectedImages, folderId, onDelete]);

  // clear states, return to simple image list display
  const returnToNormal = React.useCallback(() => {
    setSelectedImages([]);
    setFullModalEntered(false);
    setEdit(initialEditState);
    setDrawArea(null);
  }, []);

  // return to normal when image blurred
  React.useEffect(() => {
    if (removeImageLicensePlate) {
      onClearRemoveImageLicensePlate();
      setFullModalEntered(false);
      setTimeout(returnToNormal, 150);
    }
  }, [removeImageLicensePlate]); // eslint-disable-line

  // clear selection upon deletion service response
  React.useEffect(() => {
    if (!deleteImagesPending) setSelectedImages([]);
  }, [deleteImagesPending]);

  const getImageCheckHandler = imageId => e => {
    const deselect = selectedImages.includes(imageId);
    const newSelectedImages = deselect
      ? selectedImages.filter(si => si !== imageId)
      : selectedImages.concat(imageId);
    setSelectedImages(newSelectedImages);
    e.stopPropagation && e.stopPropagation();
    return false;
  };

  const getImageEditHandler = imageId => () => {
    if (!imageUrls) return;
    for (var i = 0; i < imageUrls.length; i++) {
      if (imageUrls[i].id === imageId) {
        setEdit({
          imageId,
          nextImageId: (imageUrls[i + 1] || {}).id,
          prevImageId: i > 0 && imageUrls[i - 1].id,
        });
        setSelectedImages([]);
      }
    }
  };

  // expand modal body when entering edit mode
  React.useEffect(() => {
    if (edit.imageId) setTimeout(() => setFullModalEntered(true));
  }, [edit]);

  const hide = React.useCallback(() => {
    returnToNormal();
    isFunc(onHide) && onHide();
  }, [onHide, returnToNormal]);

  const closeButton = React.useMemo(
    () => (
      <button
        type="button"
        className="btn btn-primary btn-sm float-right"
        onClick={hide}
      >
        {I.t('CLOSE')}
      </button>
    ),
    [hide]
  );

  const { handleReorder, reorderImageList } = useReorder(
    imageUrls,
    mode,
    reorderImages,
    onReorderImages,
    folderId
  );

  const buttons = React.useMemo(() => {
    if (mode === modes.Delete && selectedImages.length > 0)
      return [
        <button
          type="button"
          className="btn btn-danger btn-sm float-right ml-sm"
          onClick={handleDelete}
        >
          {I.t('DELETE')}
        </button>,
        closeButton,
      ];
    if (mode === modes.Delete || mode === modes.Reorder || !edit.imageId)
      return [closeButton];

    if (drawArea)
      return [
        <button
          type="button"
          className="btn btn-primary btn-sm float-right ml-sm"
          onClick={() => onRemoveLicensePlate(folderId, edit.imageId, drawArea)}
        >
          {I.t('SAVE')}
        </button>,
      ];

    return [
      !!edit.prevImageId && (
        <button
          className="btn btn-sm action-button"
          onClick={getImageEditHandler(edit.prevImageId)}
          type="button"
        >
          <i className="fa fa-arrow-left" />
        </button>
      ),
      <button
        className="btn btn-default btn-sm"
        onClick={() => {
          setFullModalEntered(false);
          setTimeout(returnToNormal, 150);
        }}
        type="button"
      >
        {I.t('RETURN')}
      </button>,
      !!edit.nextImageId && (
        <button
          className="btn btn-sm action-button"
          onClick={getImageEditHandler(edit.nextImageId)}
          type="button"
        >
          <i className="fa fa-arrow-right" />
        </button>
      ),
    ].filter(item => !!item);
  }, [closeButton, selectedImages, handleDelete, drawArea, edit]); // eslint-disable-line

  return (
    <Modal
      className={`edit-photo ${edit.imageId && 'full-height'} ${
        fullModalEntered && 'expanded'
      }`}
      width={`calc(100vw - 500px)`}
      style={styles.modal}
      bodyStyle={styles.modalBody}
      footer={buttons}
      title={I.t('MANAGE')}
      onCancel={hide}
      visible={show}
    >
      <LoaderOverlay
        loading={
          reorderImagesPending ||
          deleteImagesPending ||
          removeImageLicensePlatePending
        }
      />
      <div
        className={`auction-images-mode-selector ${
          edit.imageId ? 'hidden' : ''
        }`}
      >
        <Radio.Group
          buttonStyle="solid"
          defaultValue={defaultMode}
          onChange={e => setMode(e.target.value)}
        >
          <Radio.Button value={modes.Blur}>
            {I.t('LICENSE_PLATE_BLUR')}
          </Radio.Button>
          <Radio.Button value={modes.Delete}>{I.t('DELETION')}</Radio.Button>
          <Radio.Button value={modes.Reorder}>{I.t('REORDER')}</Radio.Button>
        </Radio.Group>
      </div>

      <div className={`auction-reorder-wrap ${edit.imageId ? 'hidden' : ''}`}>
        {mode === modes.Reorder ? (
          <Reorder
            callback={handleReorder}
            onReorder={handleReorder}
            reorderId="auction-images-reorder"
            style={{ overflow: 'hidden' }}
          >
            {reorderImageList}
          </Reorder>
        ) : mode === modes.Blur ? (
          imageUrls.map(u => (
            <ImageBlock
              key={u.id}
              onClick={getImageEditHandler(u.id)}
              thumbnail={u.thumbnail}
            />
          ))
        ) : (
          imageUrls.map(u => (
            <ImageBlock
              checked={selectedImages.includes(u.id)}
              key={u.id}
              onCheck={getImageCheckHandler(u.id)}
              onClick={getImageEditHandler(u.id)}
              thumbnail={u.thumbnail}
            />
          ))
        )}
      </div>
      {edit.imageId && (
        <ImageRectangleDrawer
          imageUrl={editImageUrl}
          inTransition={!fullModalEntered}
          onDraw={data => setDrawArea(data)}
          onReset={() => setDrawArea(null)}
        />
      )}
    </Modal>
  );
};

ManageImagesModal.propTypes = {
  deleteImagesPending: bool,
  imageUrls: arrayOf(
    shape({
      id: string,
      original: string,
      thumbnail: string,
    })
  ),
  onClearRemoveImageLicensePlate: func,
  onDelete: func,
  onHide: func,
  onRemoveLicensePlate: func,
  removeImageLicensePlate: object,
  removeImageLicensePlateError: object,
  removeImageLicensePlatePending: bool,
  show: bool,
};

export default ManageImagesModal;
